// Single source of truth for dashboard localStorage keys + formats.
//
// The new v2 frontend and the legacy (dx-react-grid) frontend share the
// same browser localStorage. Users keep both apps open in parallel:
// settings tweaked in one must be visible — and not corrupted — in the
// other. That means BOTH apps have to agree on:
//
//   1. The exact key strings (no `v2-dashboard-*` prefix; we keep the
//      legacy names so the old app still finds them).
//   2. The exact on-disk format for every key.
//
// Legacy formats (kept verbatim):
//   filters            → [{ columnName, value: [...] }, ...]
//   columnWidths       → [{ columnName, width },        ...]
//   columnOrder        → ["id", "po_number", ...]
//   hiddenColumnNames  → ["state", "sub_state_name", ...]
//   excludeShippedToClientOrders / excludeClosedLostOrders /
//   showShippedLast90DaysOrders  / excludeParentWithCloseGmv
//                      → JSON booleans ("true" / "false")
//
// Inside the v2 frontend we prefer record/Set shapes (faster lookups,
// easier React diffing). The helpers below convert at the LS boundary
// so the runtime stays ergonomic while the persisted shape stays
// backwards-compatible.
//
// Keys this module deliberately does NOT touch (no legacy equivalent
// in the old grid):
//   • v2-dashboard-sorting           — sorting wasn't persisted in legacy
//   • v2-dashboard-date-range-filters — date-range filter is a v2 feature
// Those two are safe v2-only keys and won't collide.

// ─── Legacy key constants ────────────────────────

export const LEGACY_KEYS = {
  filters:           'filters',
  columnWidths:      'columnWidths',
  columnOrder:       'columnOrder',
  hiddenColumnNames: 'hiddenColumnNames',
  excludeShipped:    'excludeShippedToClientOrders',
  excludeClosedLost: 'excludeClosedLostOrders',
  showShippedLast90: 'showShippedLast90DaysOrders',
  excludeParentGmv:  'excludeParentWithCloseGmv',
} as const

// ─── Low-level safe LS helpers ────────────────────

function read<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return v == null ? fallback : (JSON.parse(v) as T)
  } catch { return fallback }
}
function write(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* noop */ }
}

// ─── Booleans (1:1 shape with legacy) ────────────

export function readBool(legacyKey: string, fallback = false): boolean {
  const v = read<unknown>(legacyKey, fallback)
  return typeof v === 'boolean' ? v : fallback
}
export function writeBool(legacyKey: string, value: boolean) { write(legacyKey, value) }

export const readExcludeShipped     = (): boolean      => readBool(LEGACY_KEYS.excludeShipped)
export const writeExcludeShipped    = (v: boolean)     => writeBool(LEGACY_KEYS.excludeShipped, v)
export const readExcludeClosedLost  = (): boolean      => readBool(LEGACY_KEYS.excludeClosedLost)
export const writeExcludeClosedLost = (v: boolean)     => writeBool(LEGACY_KEYS.excludeClosedLost, v)
export const readShowShippedLast90  = (): boolean      => readBool(LEGACY_KEYS.showShippedLast90)
export const writeShowShippedLast90 = (v: boolean)     => writeBool(LEGACY_KEYS.showShippedLast90, v)
export const readExcludeParentGmv   = (): boolean      => readBool(LEGACY_KEYS.excludeParentGmv)
export const writeExcludeParentGmv  = (v: boolean)     => writeBool(LEGACY_KEYS.excludeParentGmv, v)

// ─── Column order / hidden (already arrays in both) ──

export function readColumnOrder(fallback: string[]): string[] {
  const raw = read<unknown>(LEGACY_KEYS.columnOrder, null)
  if (!Array.isArray(raw)) return fallback
  const cleaned = raw.filter((v): v is string => typeof v === 'string')
  return cleaned.length > 0 ? cleaned : fallback
}
export function writeColumnOrder(order: string[]) { write(LEGACY_KEYS.columnOrder, order) }

export function readHiddenColumnNames(fallback: string[]): string[] {
  const raw = read<unknown>(LEGACY_KEYS.hiddenColumnNames, null)
  if (!Array.isArray(raw)) return fallback
  return raw.filter((v): v is string => typeof v === 'string')
}
export function writeHiddenColumnNames(hidden: string[]) {
  write(LEGACY_KEYS.hiddenColumnNames, hidden)
}

// ─── Column widths (array <-> record) ────────────

export interface LegacyColumnWidth { columnName: string; width: number }

// Returns merged map of defaults overlaid with whatever is in LS.
// Empty LS → just defaults. Helps callers avoid an extra defaults
// merge at the use site.
export function readColumnWidths(defaults: Record<string, number>): Record<string, number> {
  const raw = read<unknown>(LEGACY_KEYS.columnWidths, null)
  const out: Record<string, number> = { ...defaults }
  if (!Array.isArray(raw)) return out
  raw.forEach(item => {
    if (item && typeof item === 'object'
        && typeof (item as LegacyColumnWidth).columnName === 'string'
        && typeof (item as LegacyColumnWidth).width === 'number') {
      out[(item as LegacyColumnWidth).columnName] = (item as LegacyColumnWidth).width
    }
  })
  return out
}

// Persist back to legacy array shape so the old app keeps working.
// Defaults are EXCLUDED from the persisted payload — only user-modified
// widths get written, which matches legacy behaviour: widths sourced
// from defaultColumnWidths.js are not redundantly stored.
export function writeColumnWidths(widths: Record<string, number>, defaults: Record<string, number>) {
  const asArray: LegacyColumnWidth[] = []
  Object.entries(widths).forEach(([columnName, width]) => {
    if (defaults[columnName] !== width) {
      asArray.push({ columnName, width })
    }
  })
  // Merge with anything already on disk (other columns the user resized
  // earlier) so we don't accidentally erase resizes for columns the v2
  // app doesn't know about yet.
  const existing = read<unknown>(LEGACY_KEYS.columnWidths, [])
  if (Array.isArray(existing)) {
    existing.forEach(item => {
      if (item && typeof item === 'object'
          && typeof (item as LegacyColumnWidth).columnName === 'string'
          && typeof (item as LegacyColumnWidth).width === 'number'
          && !(item as LegacyColumnWidth).columnName.match(/^_/) // skip junk
          && !widths[(item as LegacyColumnWidth).columnName]) {
        asArray.push(item as LegacyColumnWidth)
      }
    })
  }
  write(LEGACY_KEYS.columnWidths, asArray)
}

// ─── Filters (array of {columnName, value} <-> record of {column, values:Set}) ──

export interface LegacyFilter { columnName: string; value: string[] }

export interface FilterRecord {
  column: string
  values: Set<string>
}

export function readFilters(): Record<string, FilterRecord> {
  const raw = read<unknown>(LEGACY_KEYS.filters, null)
  const out: Record<string, FilterRecord> = {}
  if (!Array.isArray(raw)) return out
  raw.forEach(entry => {
    if (!entry || typeof entry !== 'object') return
    const e = entry as LegacyFilter
    if (typeof e.columnName !== 'string' || !Array.isArray(e.value)) return
    const values = (e.value as unknown[])
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
    if (values.length === 0) return
    out[e.columnName] = { column: e.columnName, values: new Set(values) }
  })
  return out
}

export function writeFilters(filters: Record<string, FilterRecord>) {
  const asArray: LegacyFilter[] = []
  Object.entries(filters).forEach(([col, f]) => {
    const values = [...f.values]
    if (values.length === 0) return
    asArray.push({ columnName: col, value: values })
  })
  write(LEGACY_KEYS.filters, asArray)
}

// ─── Wipe-all (Clear Settings toolbar action) ────

export function clearAllDashboardSettings() {
  Object.values(LEGACY_KEYS).forEach(key => localStorage.removeItem(key))
  // v2-only keys we still own
  localStorage.removeItem('v2-dashboard-sorting')
  localStorage.removeItem('v2-dashboard-date-range-filters')
}
